import color from 'color';
import type { FC, PropsWithChildren } from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import {
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions
} from 'react-native';
import { Button, Dialog, Text } from 'react-native-paper';

import { WEB_MAX_WIDTH } from '@/constants/web';
import { isWeb } from '@/shared/utils/platform';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const DIALOG_HORIZONTAL_MARGIN = 48;

const makeStyles = (theme: AppTheme, width: number) =>
  StyleSheet.create({
    dialog: {
      borderRadius: 28,
      backgroundColor: theme.colors.surfaceContainerHigh,
      ...(isWeb && {
        alignSelf: 'center',
        width: Math.max(
          0,
          Math.min(width, WEB_MAX_WIDTH) - DIALOG_HORIZONTAL_MARGIN
        )
      })
    },
    actions: {
      flex: 1,
      gap: 12,
      justifyContent: 'flex-end',
      flexDirection: 'row',
      flexWrap: 'wrap-reverse'
    },
    button: { paddingHorizontal: 16 }
  });

export type AlertLabels = {
  title: string;
  content?: string;
  confirm?: string;
  secondaryConfirm?: string;
  cancel?: string;
};

export type AlertProps = {
  labels: AlertLabels;
  isVisible?: boolean;
  dismissable?: boolean;
  isConfirmDisabled?: boolean;
  isConfirmLoading?: boolean;
  dismissOnConfirm?: boolean;
  confirmColor?: string;
  secondaryConfirmColor?: string;
  /** Optional style override for the scroll area wrapping the `children` body. */
  scrollAreaStyle?: StyleProp<ViewStyle>;
  onConfirm?: () => void;
  onSecondaryConfirm?: () => void;
  onCancel?: () => void;
  onClose: () => void;
};

export const Alert: FC<PropsWithChildren<AlertProps>> = ({
  labels,
  isVisible = false,
  dismissable = false,
  dismissOnConfirm = true,
  confirmColor,
  secondaryConfirmColor,
  scrollAreaStyle,
  isConfirmDisabled = false,
  isConfirmLoading = false,
  children,
  onConfirm,
  onSecondaryConfirm,
  onCancel,
  onClose
}) => {
  const theme = useAppTheme();
  const { width } = useWindowDimensions();
  const styles = useMemo(() => makeStyles(theme, width), [theme, width]);

  const { title, content, confirm, secondaryConfirm, cancel } = labels;
  const [visible, setVisible] = useState<boolean>(isVisible);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    onConfirm?.();
    if (dismissOnConfirm) handleDismiss();
  }, [dismissOnConfirm, handleDismiss, onConfirm]);

  const handleSecondaryConfirm = useCallback(() => {
    onSecondaryConfirm?.();
    if (dismissOnConfirm) handleDismiss();
  }, [dismissOnConfirm, handleDismiss, onSecondaryConfirm]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    handleDismiss();
  }, [handleDismiss, onCancel]);

  return (
    <Dialog
      onDismiss={handleDismiss}
      visible={visible}
      dismissable={dismissable || isEmptyOrNull(confirm)}
      style={styles.dialog}
    >
      <Dialog.Title>
        <Text variant="headlineSmall">{title}</Text>
      </Dialog.Title>
      {!isEmptyOrNull(content) && (
        <Dialog.Content>
          <Text variant="bodyMedium">{content}</Text>
        </Dialog.Content>
      )}
      {children && (
        <Dialog.ScrollArea style={scrollAreaStyle}>
          <ScrollView>{children}</ScrollView>
        </Dialog.ScrollArea>
      )}
      <Dialog.Actions>
        <View style={styles.actions}>
          {!isEmptyOrNull(cancel) && (
            <Button onPress={handleCancel} disabled={isConfirmLoading}>
              {cancel}
            </Button>
          )}
          {!isEmptyOrNull(confirm) && (
            <Button
              mode="contained"
              onPress={handleConfirm}
              disabled={isConfirmDisabled || isConfirmLoading}
              loading={isConfirmLoading}
              buttonColor={confirmColor ?? theme.colors.primary}
              contentStyle={styles.button}
              theme={{
                colors: {
                  surfaceDisabled: color(confirmColor ?? theme.colors.primary)
                    .alpha(0.3)
                    .string(),
                  onSurfaceDisabled: color(confirmColor ?? theme.colors.primary)
                    .alpha(0.6)
                    .string()
                }
              }}
            >
              {confirm}
            </Button>
          )}
          {!isEmptyOrNull(secondaryConfirm) && (
            <Button
              mode="contained"
              onPress={handleSecondaryConfirm}
              disabled={isConfirmDisabled}
              buttonColor={secondaryConfirmColor ?? theme.colors.primary}
              contentStyle={styles.button}
              theme={{
                colors: {
                  surfaceDisabled: color(confirmColor ?? theme.colors.primary)
                    .alpha(0.3)
                    .string(),
                  onSurfaceDisabled: color(confirmColor ?? theme.colors.primary)
                    .alpha(0.6)
                    .string()
                }
              }}
            >
              {secondaryConfirm}
            </Button>
          )}
        </View>
      </Dialog.Actions>
    </Dialog>
  );
};
